import React, {
  useState,
} from 'react';
import {
  AuthProvider,
  useAuth,
} from 'react-oidc-context';
import '@mantine/core/styles.css'; // Component Base Styles
import '@mantine/notifications/styles.css'; // Notification Styles
import '@mantine/dates/styles.css'; // Calendar Styles
import '@mantine/dropzone/styles.css'; // Dropzone Styles
import '#src/styles/fonts.scss'; // Font Styles
import '#src/styles/entry.client.scss'; // Application Styles
import {
  HTTPError,
} from 'ky';
import {
  QueryCache,
  QueryClient,
} from '@tanstack/react-query'; // Data loader
import {
  RouterProvider,
} from '@tanstack/react-router'; // Router
import {
  MantineProvider,
} from '@mantine/core'; // Theme Context

import {
  refreshAuthToken,
  shouldSkipRetry,
} from '#components/errors/QueryError'; // Default Error component
import {
  theme,
} from '#components/themes/Theme'; // Theme Instance
import {
  getOidcConfig,
} from '#features/auth/OidcConfig';
import {
  defaultQueryFn,
} from '#src/api/query/default';
import {
  TraceProvider,
  FaroAppWrapper,
} from '#src/helpers/index'; // Monitoring
import {
  type RouterType,
} from '#src/router';
import {
  type RootRouterAuthContext,
} from '#src/types/auth';

export interface AuthProvidedProps {
  router: RouterType;
}

/**
 * React function component which is the earliest descendent of the AuthProvider context
 *
 * @return {*}  {React.ReactNode}
 */
export function AuthProvided(
  props: AuthProvidedProps,
): React.ReactNode {
  const auth = useAuth() as unknown as RootRouterAuthContext;
  const {
    router,
  } = props;
  console.debug(router.state.location);
  const refreshingTokenState = useState<boolean>(false);
  // Create a new Query client instance
  const [queryClient] = useState(() => {
    console.debug('Creating QueryClient instance');
    return new QueryClient({
      defaultOptions: {
        queries: {
          queryFn: defaultQueryFn,
          staleTime: 30000, // 30 seconds
          refetchInterval: 30000, // 30 seconds
          retry: (
            failureCount: number,
            error: HTTPError,
          ) => {
            console.warn(`Query retry called. failureCount = ${failureCount}`, error);
            // Don't retry for certain error responses
            const willSkip = shouldSkipRetry(error);
            if (willSkip) {
              console.warn('Skipping query retry');
              return !willSkip;
            }
            // Retry others just once
            return (failureCount <= 1);
          },
        },
      },
      queryCache: new QueryCache({
        onError: (error: HTTPError) => {
          if (shouldSkipRetry(error)) {
            refreshAuthToken(
              auth,
              router,
              refreshingTokenState,
            );
          } else {
            console.warn('QueryCache onError called', error);
          }
        },
      }),
    });
  });

  auth.events.addUserSessionChanged(() => {
    console.debug('Got user session change event', auth);
  });
  auth.events.addSilentRenewError((error) => {
    console.error('Silent renew error:', error);
    auth.signinRedirect();
  });
  return (
    <RouterProvider
      router={ router }
      context={ {
        auth,
        queryClient,
        header: (<></>),
      } }
    />
  );
}

export interface EntryComponentProps {
  router: RouterType;
}

/**
 * React application entry function component
 *
 * @return {*}  {React.ReactNode}
 */
export function EntryComponent(
  props: EntryComponentProps,
): React.ReactNode {
  const {
    router,
  } = props;
  const oidcConfig = getOidcConfig(window.location.origin);
  return (
    <FaroAppWrapper>
      <MantineProvider
        theme={ theme }
        defaultColorScheme="dark"
      >
        <AuthProvider
          { ...oidcConfig }
        >
          <AuthProvided
            router={ router }
          />
        </AuthProvider>
      </MantineProvider>
    </FaroAppWrapper>
  );
}
